import { ItemType, PaletteColor } from "../defines/enums"
import { MapItem, Position } from "../defines/types"
import { Binder } from "../entity/items/binder"
import { Elixir } from "../entity/items/elixir"
import { Equipment } from "../entity/items/equipment"
import { Hint } from "../entity/items/hint"
import { Item } from "../entity/items/item"
import { Key } from "../entity/items/key"
import { Optic } from "../entity/items/optic"
import { getEquipment, isEquipment } from "./equipment"
import { generateRandomNumber } from "./rng"

function makeBinder(position: Position): Item {
  return new Binder(position)
}

function makeItem(config: MapItem, position?: Position): Item | undefined {
  let item = undefined

  if (isNormalHint(config.type)) {
    if (config.textContents === undefined) {
      throw new Error("Hint must have textContents")
    } else if (
      position === undefined &&
      config.row === undefined &&
      config.column === undefined
    ) {
      throw new Error("Hint must set column+row or position argument")
    } else {
      let x = 0
      let y = 0

      if (position !== undefined) {
        x = position.x + 1
        y = position.y
      } else if (config.row !== undefined && config.column !== undefined) {
        y = config.row
        x = config.column
      }

      const actualPosition = { x: x, y: y }
      item = new Hint(config.textContents, config.type, actualPosition)
    }
  } else if (config.type == ItemType.HINT_COLLECTIBLE) {
    if (position === undefined) {
      throw new Error("Collectible hint must have a position")
    } else if (!config.textContents) {
      throw new Error("Collectible hint must have textContents")
    } else {
      item = new Hint(config.textContents, config.type, position)
    }
  } else if (config.type == ItemType.ELIXIR) {
    if (position === undefined) {
      throw new Error("Elixir must have a position")
    } else {
      item = new Elixir(position)
    }
  } else if (config.type == ItemType.KEY) {
    if (config.keyTarget === undefined) {
      throw new Error("Key items must have keyTarget")
    } else if (position === undefined) {
      throw new Error("Key item not given a valid position")
    } else {
      item = new Key(config.keyTarget, position)
    }
  } else if (isOptic(config.type)) {
    if (position === undefined) {
      throw new Error("Optic must have a position")
    } else {
      item = new Optic(config.type, position)
    }
  } else if (isEquipment(config.type)) {
    if (position === undefined) {
      throw new Error("Equipment must have a position")
    } else {
      const e = getEquipment(config.type)
      if (e !== undefined) {
        const levelUnlock = e.levelUnlock ? e.levelUnlock : 1
        item = new Equipment(config.type, levelUnlock, position)
      }
    }
  }

  return item
}

function isNormalHint(type: ItemType): boolean {
  return type == ItemType.HINT_INFO || type == ItemType.HINT_WARNING
}

function isOptic(type: ItemType): boolean {
  return (
    type == ItemType.OPTIC_RED ||
    type == ItemType.OPTIC_GREEN ||
    type == ItemType.OPTIC_BLUE ||
    type == ItemType.OPTIC_MAGENTA ||
    type == ItemType.OPTIC_CYAN ||
    type == ItemType.OPTIC_YELLOW
  )
}

function shouldPlaceRandomly(item: MapItem): boolean {
  return !item.dropNearPlayer && !item.column && !item.row
}

function isPigment(type: ItemType): boolean {
  return (
    type == ItemType.PIGMENT_RED ||
    type == ItemType.PIGMENT_GREEN ||
    type == ItemType.PIGMENT_BLUE ||
    type == ItemType.PIGMENT_MAGENTA ||
    type == ItemType.PIGMENT_CYAN ||
    type == ItemType.PIGMENT_YELLOW
  )
}

function convertToPigment(item: ItemType): PaletteColor {
  let pigment = PaletteColor.YELLOW

  switch (item) {
    case ItemType.PIGMENT_RED:
    case ItemType.OPTIC_RED:
      pigment = PaletteColor.RED
      break
    case ItemType.PIGMENT_MAGENTA:
    case ItemType.OPTIC_MAGENTA:
      pigment = PaletteColor.MAGENTA
      break
    case ItemType.PIGMENT_BLUE:
    case ItemType.OPTIC_BLUE:
      pigment = PaletteColor.BLUE
      break
    case ItemType.PIGMENT_CYAN:
    case ItemType.OPTIC_CYAN:
      pigment = PaletteColor.CYAN
      break
    case ItemType.PIGMENT_GREEN:
    case ItemType.OPTIC_GREEN:
      pigment = PaletteColor.GREEN
      break
    default:
      break
  }

  return pigment
}

function getPigmentForOptic(optic: ItemType): ItemType {
  switch (optic) {
    case ItemType.OPTIC_RED:
      return ItemType.PIGMENT_RED
    case ItemType.OPTIC_BLUE:
      return ItemType.PIGMENT_BLUE
    case ItemType.OPTIC_GREEN:
      return ItemType.PIGMENT_GREEN
    case ItemType.OPTIC_MAGENTA:
      return ItemType.PIGMENT_MAGENTA
    case ItemType.OPTIC_CYAN:
      return ItemType.PIGMENT_CYAN
    case ItemType.OPTIC_YELLOW:
      return ItemType.PIGMENT_YELLOW
  }

  return ItemType.OPTIC_NONE
}

function getRandomPigment(): string {
  const pigments = ["red", "green", "blue", "yellow", "magenta", "cyan"]

  return pigments[generateRandomNumber(0, pigments.length - 1)]
}

export {
  convertToPigment,
  getPigmentForOptic,
  getRandomPigment,
  makeBinder,
  makeItem,
  shouldPlaceRandomly,
}
